import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import EntityService from '../../services/EntityService';
import { API } from '../../shared/api/Api';

/**
 * Fetch all placement tests
 */
export const fetchPlacementTests = createAsyncThunk(
  'placementTests/fetchPlacementTests',
  async ({ token, params }, { rejectWithValue }) => {
    try {
      const response = await EntityService.getAllEnties(
        token,
        API.PlacementTests.all,
        params
      );
      if (response.data.status === 200 && response.data.errorCode === 0) {
        return {
          items: response.data.data.placement_tests,
          currentPage: response.data.data.pagination.current_page,
          totalPages: response.data.data.pagination.total_pages,
          total: response.data.data.pagination.total,
        };
      }
      return rejectWithValue(response.data.message || 'Failed to fetch placement tests');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch placement tests');
    }
  }
);

/**
 * Create a new placement test
 */
export const createPlacementTest = createAsyncThunk(
  'placementTests/createPlacementTest',
  async ({ token, formData }, { rejectWithValue }) => {
    try {
      const response = await EntityService.addEntity(
        token,
        API.PlacementTests.add,
        formData
      );
      if (response.data.status === 200 || response.data.status === 201) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to create placement test');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to create placement test';
      const validationErrors = error.response?.data?.data || {};
      return rejectWithValue({ message: errorMessage, errors: validationErrors });
    }
  }
);

/**
 * Enrollment in placement test
 */
export const enrollPlacementTest = createAsyncThunk(
  'placementTests/enroll',
  async ({ token, data }, { rejectWithValue }) => {
    try {
      const response = await EntityService.addEntity(
        token,
        API.PlacementTests.enroll,
        data
      );
      if (response.data.status === 200 || response.data.status === 201) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to enroll');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to enroll');
    }
  }
);

const initialState = {
  entities: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    total: 0,
  },
  listStatus: 'idle',
  createStatus: 'idle',
  enrollStatus: 'idle',
  listError: null,
  createError: null,
  enrollError: null,
  formErrors: {},
};

const placementTestSlice = createSlice({
  name: 'placementTests',
  initialState,
  reducers: {
    clearFormErrors: (state) => {
      state.formErrors = {};
      state.createError = null;
    },
    resetStatus: (state) => {
      state.createStatus = 'idle';
      state.enrollStatus = 'idle';
      state.createError = null;
      state.enrollError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPlacementTests.pending, (state) => {
        state.listStatus = 'loading';
      })
      .addCase(fetchPlacementTests.fulfilled, (state, action) => {
        state.listStatus = 'succeeded';
        state.entities = action.payload.items;
        state.pagination = {
          currentPage: action.payload.currentPage,
          totalPages: action.payload.totalPages,
          total: action.payload.total,
        };
      })
      .addCase(fetchPlacementTests.rejected, (state, action) => {
        state.listStatus = 'failed';
        state.listError = action.payload;
      })
      .addCase(createPlacementTest.pending, (state) => {
        state.createStatus = 'loading';
      })
      .addCase(createPlacementTest.fulfilled, (state) => {
        state.createStatus = 'succeeded';
      })
      .addCase(createPlacementTest.rejected, (state, action) => {
        state.createStatus = 'failed';
        state.createError = action.payload.message;
        state.formErrors = action.payload.errors || {};
      });
  }
});

export const { clearFormErrors, resetStatus } = placementTestSlice.actions;

export const selectPlacementTests = (state) => state.placementTests.entities;
export const selectPlacementTestsPagination = (state) => state.placementTests.pagination;
export const selectPlacementTestListStatus = (state) => state.placementTests.listStatus;
export const selectPlacementTestCreateStatus = (state) => state.placementTests.createStatus;
export const selectPlacementTestFormErrors = (state) => state.placementTests.formErrors;

export default placementTestSlice.reducer;
